<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  clinicalRecordVoidsApi,
  type ClinicalCorrection,
  type ClinicalVoidItemType,
} from '@/api/clinical-record-voids';
import { apiErrorStatus, apiErrorText } from '@/utils/api-errors';
import { authorIdentityErrorText } from '@/utils/author-identity';
import { type ClinicalCorrectionKind } from '@/utils/clinical-void';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

const props = withDefaults(defineProps<{
  itemType: ClinicalVoidItemType;
  itemId: number;
  // What the registro is ("Registro escrito · 10/03 14:00").
  label: string;
  correction?: ClinicalCorrectionKind | null;
  // The text the corrección starts from.
  currentText?: string | null;
}>(), { correction: null, currentText: null });

const emit = defineEmits<{ voided: [] }>();

const open = defineModel<boolean>({ default: false });

// Every list a registro clínico shows up in reloads after an anulación.
const CLINICAL_QUERY_KEYS = [
  'shift', 'clinicalRecords', 'vitalSignRecords', 'groupedVitalSignRecords',
  'latestVitalSignRecords', 'taskRecords', 'incidentRecords', 'notes', 'patientDocuments',
  'clinicalRecordHistory',
];

// VoidableClinicalRecord::VOID_REASON_MAX_LENGTH
const REASON_MAX_LENGTH = 1000;

const reason = ref('');
const reasonError = ref('');
const correctionError = ref('');
const withCorrection = ref(false);
const correctionText = ref('');
const correctionValue = ref('');
const systolic = ref('');
const diastolic = ref('');

function reset() {
  reason.value = '';
  reasonError.value = '';
  correctionError.value = '';
  withCorrection.value = false;
  correctionText.value = props.currentText ?? '';
  correctionValue.value = '';
  systolic.value = '';
  diastolic.value = '';
}

watch(open, (isOpen) => {
  if (isOpen) reset();
}, { immediate: true });

const correctionLabel = computed(() => (
  props.correction === 'text' ? 'Registrar el texto correcto' : 'Registrar el valor correcto'
));

function correctionPayload(): ClinicalCorrection | null {
  if (!props.correction || !withCorrection.value) return null;
  if (props.correction === 'text') return { text: correctionText.value };
  if (props.correction === 'value') return { value: correctionValue.value.replace(',', '.') };

  return { systolic: systolic.value, diastolic: diastolic.value };
}

const queryClient = useQueryClient();

const { mutate, isPending, error, reset: resetMutation } = useMutation({
  mutationFn: () => clinicalRecordVoidsApi.create({
    itemType: props.itemType,
    itemId: props.itemId,
    reason: reason.value.trim(),
    correction: correctionPayload(),
  }),
  onSuccess: () => {
    CLINICAL_QUERY_KEYS.forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
    emit('voided');
    open.value = false;
  },
});

const UNAUTHORIZED = 401;

const errorText = computed(() => {
  if (!error.value) return '';
  if (apiErrorStatus(error.value) === UNAUTHORIZED) {
    return 'No tienes permiso para anular este registro.';
  }

  return authorIdentityErrorText(error.value) ||
    apiErrorText(error.value, 'No se pudo anular el registro. Intenta nuevamente.');
});

function isNumber(value: string): boolean {
  return value.trim() !== '' && Number.isFinite(Number(value.replace(',', '.')));
}

// Each kind of corrección: whether it is complete, and what to ask otherwise.
const CORRECTION_CHECKS: Record<ClinicalCorrectionKind, { complete: () => boolean; message: string }> = {
  text: { complete: () => correctionText.value.trim() !== '', message: 'Escribe el texto correcto.' },
  value: { complete: () => isNumber(correctionValue.value), message: 'Indica el valor correcto.' },
  bloodPressure: {
    complete: () => isNumber(systolic.value) && isNumber(diastolic.value),
    message: 'Indica la presión sistólica y diastólica.',
  },
};

// What the corrección is missing, or '' when it is complete.
function correctionProblem(): string {
  if (!props.correction || !withCorrection.value) return '';
  const check = CORRECTION_CHECKS[props.correction];

  return check.complete() ? '' : check.message;
}

function submit() {
  reasonError.value = reason.value.trim() ? '' : 'Indica por qué se anula el registro.';
  correctionError.value = correctionProblem();
  if (reasonError.value || correctionError.value) return;

  resetMutation();
  mutate();
}
</script>
<template>
  <responsive-dialog
    v-model="open"
    title="Anular registro"
    max-width="560"
    scrollable
  >
    <p class="text-subtitle-2 text-medium-emphasis mb-2">
      {{ props.label }}
    </p>
    <p class="text-body-2 mb-4">
      El registro queda en la ficha marcado como anulado, con tu nombre, la fecha y el motivo.
      No se puede deshacer.
    </p>
    <v-form @submit.prevent="submit">
      <v-textarea
        v-model="reason"
        label="Motivo de la anulación"
        variant="outlined"
        rows="2"
        auto-grow
        aria-required="true"
        :maxlength="REASON_MAX_LENGTH"
        counter
        :error-messages="reasonError"
        :disabled="isPending"
      />
      <template v-if="props.correction">
        <v-checkbox
          v-model="withCorrection"
          :label="correctionLabel"
          density="compact"
          hide-details
          :disabled="isPending"
        />
        <template v-if="withCorrection">
          <v-textarea
            v-if="props.correction === 'text'"
            v-model="correctionText"
            label="Texto correcto"
            variant="outlined"
            rows="3"
            auto-grow
            class="mt-2"
            aria-required="true"
            :error-messages="correctionError"
            :disabled="isPending"
          />
          <v-text-field
            v-else-if="props.correction === 'value'"
            v-model="correctionValue"
            label="Valor correcto"
            variant="outlined"
            inputmode="decimal"
            class="mt-2"
            aria-required="true"
            :error-messages="correctionError"
            :disabled="isPending"
          />
          <div
            v-else
            class="d-flex ga-2 mt-2"
          >
            <v-text-field
              v-model="systolic"
              label="Sistólica"
              variant="outlined"
              inputmode="numeric"
              aria-required="true"
              :error-messages="correctionError"
              :disabled="isPending"
            />
            <v-text-field
              v-model="diastolic"
              label="Diastólica"
              variant="outlined"
              inputmode="numeric"
              aria-required="true"
              :error="!!correctionError"
              :disabled="isPending"
            />
          </div>
          <p class="text-caption text-medium-emphasis">
            La corrección queda como un registro nuevo a tu nombre, que reemplaza al anulado.
          </p>
        </template>
      </template>
      <v-alert
        v-if="errorText"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-4"
      >
        {{ errorText }}
      </v-alert>
    </v-form>
    <template #actions="{ close }">
      <v-btn
        variant="text"
        :disabled="isPending"
        @click="close"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="error"
        variant="flat"
        :loading="isPending"
        @click="submit"
      >
        Anular
      </v-btn>
    </template>
  </responsive-dialog>
</template>
